// ======================================================================
// FILE:
// src/modules/expense_management/claims/components/ExpenseItemsGrid.jsx
//
// MODULE:
// Expense Management
//
// FEATURE:
// Expense Items Grid
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import expenseClaimService from "../services/expenseClaimService";

export default function ExpenseItemsGrid({

  rows = [],

  onChange,
   readOnly = false,

}) {

  const createEmptyRow = () => ({

  id: Date.now(),

  expense_date: "",

  expense_category_id: "",

  expense_type_id: "",

  vendor_id: "",

  project_id: "",

  cost_center_id: "",

  amount: "",

  tax_amount: 0,

  total_amount: 0,

  receipt: null,

  isEditing: true,

});


// ==========================================================
// UPDATE ROW
// ==========================================================

function updateRow(
  rowId,
  field,
  value
) {

  const updatedRows = rows.map((row) => {

    if (row.id !== rowId) {
      return row;
    }

    const updatedRow = {

      ...row,

      [field]: value,

    };

    const amount =
      Number(updatedRow.amount || 0);

    const tax =
      Number(updatedRow.tax_amount || 0);

    updatedRow.total_amount =
      amount + tax;

    return updatedRow;

  });

  onChange(updatedRows);

}

// ==========================================================
// SAVE ROW
// ==========================================================

function saveRow(rowId) {

  const updatedRows = rows.map((row) => {

    if (row.id !== rowId) {

      return row;

    }

    const category =
      expenseCategories.find(
        (x) =>
          Number(x.expense_category_id) ===
          Number(row.expense_category_id)
      );

    const expenseType =
      expenseTypes.find(
        (x) =>
          Number(x.expense_type_id) ===
          Number(row.expense_type_id)
      );

    const vendor =
      vendors.find(
        (x) =>
          Number(x.vendor_id) ===
          Number(row.vendor_id)
      );

    const project =
      projects.find(
        (x) =>
          Number(x.project_id) ===
          Number(row.project_id)
      );

    const costCenter =
      costCenters.find(
        (x) =>
          Number(x.cost_center_id) ===
          Number(row.cost_center_id)
      );

    return {

      ...row,

      category_name:
        category?.name || "",

      expense_type_name:
        expenseType?.name || "",

      vendor_name:
        vendor?.name || "",

      project_name:
        project?.name || "",

      cost_center_name:
        costCenter?.name || "",

      isEditing: false,

    };

  });

  onChange(updatedRows);

}
// ==========================================================
// CANCEL ROW
// ==========================================================

function cancelRow(rowId) {

  const updatedRows =
    rows.filter((row) => {

      if (
        row.id !== rowId
      ) {

        return true;

      }

      return !row.isEditing;

    });

  onChange(updatedRows);

}

// ==========================================================
// EDIT ROW
// ==========================================================

function editRow(rowId) {

  const updatedRows =
    rows.map((row) =>

      row.id === rowId
        ? {
            ...row,
            isEditing: true,
          }
        : row

    );

  onChange(updatedRows);

}

// ==========================================================
// DELETE ROW
// ==========================================================

function deleteRow(rowId) {

  if (
    !window.confirm(
      "Delete this expense item?"
    )
  ) {

    return;

  }

  onChange(

    rows.filter(
      (row) =>
        row.id !== rowId
    )

  );

}

function handleAddItem() {

  onChange?.([

    createEmptyRow(),

    ...rows,

  ]);

}


// ==========================================================
// MASTER DATA
// ==========================================================

const [
  expenseCategories,
  setExpenseCategories,
] = useState([]);

const [
  expenseTypes,
  setExpenseTypes,
] = useState([]);

const [
  vendors,
  setVendors,
] = useState([]);

const [
  projects,
  setProjects,
] = useState([]);

const [
  costCenters,
  setCostCenters,
] = useState([]);

useEffect(() => {

  loadExpenseCategories();
   loadExpenseTypes();
    loadVendors();
    loadProjects();
     loadCostCenters();

}, []);

async function loadVendors() {

  try {

    const data =
      await expenseClaimService
        .getVendors();

    setVendors(data);

  }

  catch (err) {

    console.error(
      "Failed to load Vendors",
      err
    );

  }

}

async function loadExpenseTypes() {

  try {

    const data =
      await expenseClaimService
        .getExpenseTypes();

    setExpenseTypes(data);

  }

  catch (err) {

    console.error(
      "Failed to load Expense Types",
      err
    );

  }

}
async function loadCostCenters() {

  try {

    const data =
      await expenseClaimService
        .getCostCenters();

    setCostCenters(data);

  }

  catch (err) {

    console.error(
      "Failed to load Cost Centers",
      err
    );

  }

}
async function loadProjects() {

  try {

    const data =
      await expenseClaimService
        .getProjects();

    setProjects(data);

  }

  catch (err) {

    console.error(
      "Failed to load Projects",
      err
    );

  }

}
async function loadExpenseCategories() {

  try {

    const data =
      await expenseClaimService
        .getExpenseCategories();

    setExpenseCategories(data);

  }

  catch (err) {

    console.error(
      "Failed to load Expense Categories",
      err
    );

  }

}
// ==========================================================
// GRAND TOTAL
// ==========================================================

const grandTotal = rows.reduce(

  (total, row) =>

    total + Number(row.total_amount || 0),

  0

);

return (

    <div className="card shadow-sm mb-4">

      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <div
        className="
          card-header
          d-flex
          justify-content-between
          align-items-center
        "
      >

        <h5 className="mb-0">

          Expense Items

        </h5>

        {!readOnly && (

          <button
            className="btn btn-primary btn-sm"
            onClick={handleAddItem}
          >

            <i className="fas fa-plus me-2" />

            Add Item

          </button>

          )}

      </div>

      {/* ====================================================== */}
      {/* TABLE */}
      {/* ====================================================== */}

      <div className="table-responsive">

        <table
          className="
            table
            table-bordered
            table-hover
            mb-0
          "
        >

          <thead
            className="table-light"
          >

            <tr>

              <th width="110">

                Date

              </th>

              <th>

                Category

              </th>

              <th>

                Expense Type

              </th>

              <th>

                Vendor

              </th>

              <th>

                Project

              </th>

              <th>

                Cost Center

              </th>

              <th className="text-end">

                Amount

              </th>

              <th className="text-end">

                Tax

              </th>

              <th className="text-end">

                Total

              </th>

              <th width="130">

                Receipt

              </th>

              {!readOnly && (

<th width="120">

  Actions

</th>

)}

            </tr>

          </thead>

            <tbody>

  {rows.length === 0 && (

    <tr>

      <td
        colSpan="11"
        className="text-center py-5"
      >

        No expense items added.

        <br />

        Click <strong>Add Item</strong> to begin.

      </td>

    </tr>

  )}

  {rows.map((row) => (

    <tr key={row.id}>

      
      <td>

    {row.isEditing ? (

        <input
            type="date"
            className="form-control form-control-sm"
            value={row.expense_date || ""}
            onChange={(e) =>
                updateRow(
                    row.id,
                    "expense_date",
                    e.target.value
                )
            }
        />

    ) : (

        row.expense_date

    )}

</td>

      <td>

        {row.isEditing ? (

          <select
            className="form-select form-select-sm"
            value={row.expense_category_id}
            onChange={(e) =>
                updateRow(
                    row.id,
                    "expense_category_id",
                    e.target.value
                )
            }
        >

            <option value="">
                Select Category
            </option>

            {expenseCategories.map((category) => (

              <option
                key={category.expense_category_id}
                value={category.expense_category_id}
              >

                {category.name}

              </option>

            ))}

        </select>

        ) : (

          row.category_name

        )}

      </td>

      <td>

        {row.isEditing ? (

          <select
            className="form-select form-select-sm"
            value={row.expense_type_id}
            onChange={(e) =>
                updateRow(
                    row.id,
                    "expense_type_id",
                    e.target.value
                )
            }
        >

            <option value="">
    Select Expense Type
</option>

{expenseTypes
    .filter((type) => {

        if (!row.expense_category_id) {

            return true;

        }

        return (
            Number(type.expense_category_id) ===
            Number(row.expense_category_id)
        );

    })
    .map((type) => (

        <option
            key={type.expense_type_id}
            value={type.expense_type_id}
        >

            {type.name}

        </option>

))}

        </select>

        ) : (

          row.expense_type_name

        )}

      </td>

      <td>

        {row.isEditing ? (

          <select
    className="form-select form-select-sm"
    value={row.vendor_id}
    onChange={(e) =>
        updateRow(
            row.id,
            "vendor_id",
            e.target.value
        )
    }
>

    <option value="">
        Select Vendor
    </option>
    {vendors.map((vendor) => (

    <option
        key={vendor.vendor_id}
        value={vendor.vendor_id}
    >

        {vendor.name}

    </option>

))}

</select>

        ) : (

          row.vendor_name

        )}

      </td>

      <td>

        {row.isEditing ? (

         <select
    className="form-select form-select-sm"
    value={row.project_id}
    onChange={(e) =>
        updateRow(
            row.id,
            "project_id",
            e.target.value
        )
    }
>

    <option value="">
        Select Project
    </option>
    {projects.map((project) => (

    <option
        key={project.project_id}
        value={project.project_id}
    >

        {project.name}

    </option>

))}

</select>

        ) : (

          row.project_name

        )}

      </td>

      <td>

        {row.isEditing ? (

         <select
    className="form-select form-select-sm"
    value={row.cost_center_id}
    onChange={(e) =>
        updateRow(
            row.id,
            "cost_center_id",
            e.target.value
        )
    }
>

    <option value="">
        Select Cost Center
    </option>
    {costCenters.map((costCenter) => (

    <option
        key={costCenter.cost_center_id}
        value={costCenter.cost_center_id}
    >

        {costCenter.name}

    </option>

))}

</select>
        ) : (

          row.cost_center_name

        )}

      </td>

      <td className="text-end">

        {row.isEditing ? (

          <input
            type="number"
            className="form-control form-control-sm text-end"
            value={Number(row.amount || 0).toFixed(2)}
            onChange={(e) =>
                updateRow(
                row.id,
                "amount",
                e.target.value
                )
            }
            />

        ) : (

          row.amount

        )}

      </td>

      <td>

  {row.isEditing ? (

    <input
      type="number"
      className="form-control form-control-sm text-end"
      value={row.tax_amount ?? 0}
      onChange={(e) =>
        updateRow(
          row.id,
          "tax_amount",
          e.target.value
        )
      }
    />

  ) : (

    Number(row.tax_amount || 0).toFixed(2)

  )}

</td>


      <td className="text-end">

        {Number(
  row.total_amount || 0
).toFixed(2)}


      </td>

      <td>

        {row.isEditing ? (

  <button
    className="
      btn
      btn-outline-secondary
      btn-sm
    "
  >

    <i className="fas fa-paperclip me-1" />

    Upload

  </button>

) : (

  row.receipt
    ? "Attached"
    : "-"

)}

      </td>
      {!readOnly && (
      <td>

        {row.isEditing ? (

          <>

            <button
                className="btn btn-success btn-sm me-1"
                onClick={() => saveRow(row.id)}
                >
                <i className="fas fa-save" />
                </button>

            <button
            className="btn btn-secondary btn-sm"
            onClick={() => cancelRow(row.id)}
            >
            <i className="fas fa-times" />
            </button>

          </>

        ) : (

          <>

            <button
            className="btn btn-primary btn-sm me-1"
            onClick={() => editRow(row.id)}
            >
            <i className="fas fa-edit" />
            </button>

            <button
            className="btn btn-danger btn-sm"
            onClick={() => deleteRow(row.id)}
            >
            <i className="fas fa-trash" />
            </button>

          </>

        )}

      </td>
    )}
    </tr>

  ))}

</tbody>

        </table>

      </div>

      {/* ====================================================== */}
      {/* FOOTER */}
      {/* ====================================================== */}

      <div className="card-footer">

        <div className="row">

          <div className="col-md-9">

            Total Items :

            {" "}

            <strong>

              {rows.length}

            </strong>

          </div>

          <div
            className="
              col-md-3
              text-end
            "
          >

            Grand Total :

            {" "}

            <strong>

              ₹ {grandTotal.toFixed(2)}

            </strong>

          </div>

        </div>

      </div>

    </div>

  );

}